// Editing the graph: a node's content, adding a node, deleting nodes and
// edges, and pushing a node's output to the nodes it feeds.
import React, { useCallback } from "react";
import type { Edge, Node, NodeChange, ReactFlowInstance } from "reactflow";
import { NodeType } from "../../constants";
import { TrillGenerator } from "../../TrillGenerator";
import {
    ensureSlotArrays,
    inputSlotOf,
    setSlot,
    slotsFedBy,
    portsFedBy,
    withPortValues,
    wiredInputSlots,
    nodeInputFromSlots,
    compactedHandles,
    withoutSlot,
} from "../../utils/inputSlots";
import { renumberInputReferences } from "../../utils/references/codeReferences";
import { getUnversionedFlowNodeType } from "../../utils/flowNodeCanonicalType";
import { normalizeFlowInput } from "../../utils/flowOutputRef";
import { markSelectionEcho, SelectionEchoOptions } from "../../utils/selectionEcho";
import { nodeCodeWriter } from "../../utils/nodeCodeWriters";
import { nodeCode } from "../../utils/editFeatures/mapLayerEdits";
import type { IOutput } from "./flowTypes";
import { nodeGrowsInputs, nodeHasSeveralPorts } from "./growingInputs";

/** A growing node's data with *value* from *sourceId* in each of *slots*. */
function withSlotValues(node: Node, slots: number[], value: unknown, sourceId: string, edges: any[]): Node {
    const { inputList, sourceList } = ensureSlotArrays(node.data.inputSlots, node.data.sourceSlots);
    for (const slot of slots) setSlot(inputList, sourceList, slot, value, sourceId);
    const wired = Array.from(new Set([...wiredInputSlots(edges, node.id), ...slots])).sort((a, b) => a - b);
    return {
        ...node,
        data: { ...node.data, inputSlots: inputList, sourceSlots: sourceList, input: nodeInputFromSlots(inputList, wired) },
    };
}

/** What an agent's applied code asks of the dataflow's doc the canvas is
 *  bound to (`useDataflowDoc`). */
export interface AppliedCodeDoc {
    /** Whether the canvas is bound to the doc. */
    bound(): boolean;
}

export function useGraphEdits({
    setNodes, setEdges, reactFlow, workflowNameRef, outputsRef, markNodeStaleRef, setOutputs,
    agentEditProv, markDirtyRef, codeDocRef,
}: {
    setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
    reactFlow: ReactFlowInstance;
    workflowNameRef: React.MutableRefObject<string>;
    outputsRef: React.MutableRefObject<IOutput[]>;
    markNodeStaleRef: React.MutableRefObject<(nodeId: string) => void>;
    setOutputs: (fnOrValue: ((prev: IOutput[]) => IOutput[]) | IOutput[]) => void;
    /**
     * Files an agent's applied change to a node with no writer of its own in
     * its Provenance drawer (ProvenanceProvider's `agentEditProv`), which
     * FlowProvider passes.
     */
    agentEditProv?: (nodeId: string, code: string, agent: string, before?: string) => void;
    /** Leaves the dataflow Unsaved, so the next save writes what changed: an entry filed here among it. */
    markDirtyRef?: React.MutableRefObject<() => void>;
    /**
     * The dataflow's doc this canvas is bound to (`useDataflowDoc`): an
     * agent's applied code is already the doc's there, written by the server.
     */
    codeDocRef?: React.MutableRefObject<AppliedCodeDoc | null>;
}) {
    // The bridge's content path (dev/51): both fields, one provider-state
    // update — ``defaultCode`` drives the Monaco editor's value and ``code``
    // is what TrillGenerator serializes on save. The change is an entry of
    // the node's Provenance drawer named after *author*, the agent that wrote
    // the code. A node whose code is its state (a Map Editor node) takes it
    // through its own writer, as its own changes are made; any other node
    // takes the code as it is, and the change is filed as that writer files
    // it: an edit holding the code it left and the code it replaced, with the
    // dataflow left Unsaved, so the next save keeps the entry where an agent
    // reads it; nothing when the code stays as it was.
    const applyNodeContent = useCallback(
        (nodeId: string, content: string, author?: string) => {
            const label = author || "Agent";
            // On a canvas bound to the dataflow's doc, the server wrote the
            // agent's code into the doc, which brings it to every tab, this
            // one too, and recorded the change in the dataflow's history:
            // written here as well, the two writes of one text would merge
            // into two copies.
            if (codeDocRef?.current?.bound()) return;
            const writer = nodeCodeWriter(nodeId);
            if (writer) {
                writer(content, label);
                return;
            }
            const live = reactFlow.getNodes().find((n) => n.id === nodeId);
            setNodes((nds: Node[]) =>
                nds.map((n) =>
                    n.id === nodeId
                        ? { ...n, data: { ...n.data, code: content, defaultCode: content } }
                        : n,
                ),
            );
            if (!live) return;
            const before = nodeCode(live.data);
            if (content === before) return;
            agentEditProv?.(nodeId, content, label, before);
            markDirtyRef?.current();
        },
        [setNodes, reactFlow, agentEditProv, markDirtyRef, codeDocRef],
    );

    const addNode = useCallback(
        (node: Node, customWorkflowName?: string, provenance?: boolean) => {
            console.log("add node");
            // A node the canvas holds already is not added again: the doc and
            // an agent's apply can both bring one before the canvas renders.
            setNodes((prev: any) => (prev.some((n: Node) => n.id === node.id) ? prev : prev.concat(node)));

            if (provenance) {
                TrillGenerator.addNewVersionProvenance(
                    [...reactFlow.getNodes(), node],
                    reactFlow.getEdges(),
                    workflowNameRef.current, "", "Node added"
                );
            }
        },
        [setNodes]
    );

    /**
     * Push a source node's cached output to every direct downstream consumer.
     *
     * *edgesOverride* names the graph to read instead of React Flow's store.
     * The store is written from an effect, so right after a load it is still a
     * render behind and reports no edges at all; a restore that read it then
     * silently reached nobody. On the canvas that showed up as nodes waiting for
     * a Play, which hid it. The dashboard has no Play, so the caller passes the
     * edges the load just built.
     */
    const propagateDownstreamInputs = (
        sourceId: string,
        rawOutput: unknown,
        edgesOverride?: readonly { source?: unknown; target?: unknown; sourceHandle?: unknown; targetHandle?: unknown }[],
        options?: SelectionEchoOptions,
    ) => {
        const currentEdges = (edgesOverride ?? reactFlow.getEdges()) as any[];
        const nodesAffected: string[] = [];
        for (const edge of currentEdges) {
            if (edge.sourceHandle == "in/out" && edge.targetHandle == "in/out") continue;
            if (sourceId == edge.source) {
                nodesAffected.push(edge.target);
            }
        }
        if (!nodesAffected.length) return;

        const normalized = normalizeFlowInput(rawOutput);
        const inputPayload = normalized === "" ? "" : normalized;
        // Tag this delivery, not the output: normalizeFlowInput returns a fresh
        // object, so the cached output a later connection reads stays untagged
        // and draws like any new input.
        if (options?.selectionEcho && inputPayload !== "") markSelectionEcho(inputPayload, options.selectionSource);

        setNodes((nds: any) =>
            nds.map((node: any) => {
                if (!nodesAffected.includes(node.id)) return node;

                if (nodeGrowsInputs(node)) {
                    // Every circle this source feeds; the node reads them as one
                    // value once every wired circle holds one.
                    const prior = ensureSlotArrays(node.data.inputSlots, node.data.sourceSlots).sourceList;
                    const slots = slotsFedBy(currentEdges, node.id, sourceId, prior);
                    return withSlotValues(node, slots, inputPayload, sourceId, currentEdges);
                }

                const data = inputPayload === ""
                    ? { ...node.data, input: "", source: "" }
                    : { ...node.data, input: inputPayload, source: sourceId };
                // `input` names only the latest arrival: two that land in one
                // render leave it the second. A node with several ports reads
                // what each of them holds.
                if (nodeHasSeveralPorts(node)) {
                    data.portInputs = withPortValues(node.data.portInputs, portsFedBy(currentEdges, node.id, sourceId), inputPayload);
                }
                return { ...node, data };
            })
        );
    };

    /**
     * Hand *outId*'s output to *inId* alone, along a new edge: the one hand-over
     * a new connection makes. *edges* is the graph the edge joins, which tells
     * a node with several circles which of them are wired; the new edge is not
     * in it yet, and its circle counts as wired.
     */
    const applyOutput = (
        inNodeType: NodeType,
        inId: string,
        outId: string,
        sourceHandle: string,
        targetHandle: string,
        edges: Edge[],
    ) => {
        if (sourceHandle == "in/out" && targetHandle == "in/out") return;

        // Why: previously this read `outputs` via a side effect inside a
        // setOutputs reducer. React Flow's setNodes (zustand) flushes its
        // reducer immediately, but React's useState setOutputs queues its
        // reducer for later — so setNodes ran first with `output = ""` and
        // wrote an empty data.input, then setOutputs ran and mutated
        // `output` after no one was reading it. Read synchronously from
        // outputsRef instead.
        const entry = outputsRef.current.find((opt: any) => opt.nodeId === outId);
        const raw = entry?.output;
        const normalized =
            raw != null && raw !== "" ? normalizeFlowInput(raw) : "";

        setNodes((nds: any) =>
            nds.map((node: any) => {
                if (node.id !== inId) return node;

                if (nodeGrowsInputs(node)) {
                    const slot = inputSlotOf(targetHandle);
                    return slot >= 0 ? withSlotValues(node, [slot], normalized, outId, edges) : node;
                }

                const data = { ...node.data, input: normalized, source: outId };
                if (nodeHasSeveralPorts(node)) {
                    data.portInputs = withPortValues(node.data.portInputs, [targetHandle ?? "in"], normalized);
                }
                return { ...node, data };
            })
        );
    };

    /**
     * A growing node's circles close up after some of its edges were deleted:
     * each surviving edge moves up one circle per deleted circle above it, the
     * values move with it, and the node's code is rewritten so every input
     * chip and typed input name still names the same input. Deleted inputs'
     * chips and names become `[!! input_? !!]`. Other tabs get the moved edges
     * and the new code through the dataflow's doc; they never rewrite on their
     * own, so the code changes once.
     */
    const closeUpCircles = (target: Node, deleted: Edge[], deletedIds: Set<string>) => {
        const removed = deleted
            .map((e) => inputSlotOf(e.targetHandle))
            .filter((s) => s >= 0)
            .sort((a, b) => b - a);
        if (removed.length === 0) return;
        const survivors = reactFlow.getEdges().filter((e) => !deletedIds.has(e.id));
        const moved = new Map<string, string>();
        const handles = new Map(
            survivors.filter((e) => e.target === target.id).map((e) => [e.id, e.targetHandle ?? "in"] as [string, string]),
        );
        for (const slot of removed) {
            const step = compactedHandles(
                Array.from(handles, ([id, targetHandle]) => ({ id, target: target.id, targetHandle })),
                target.id,
                slot,
            );
            step.forEach((handle, id) => {
                handles.set(id, handle);
                moved.set(id, handle);
            });
        }

        const code: string = typeof target.data?.code === "string" ? target.data.code : "";
        // A spec's input names sit in its strings, which a code scan skips.
        const language = getUnversionedFlowNodeType(target) === NodeType.JS_COMPUTATION ? "javascript" : "python";
        // Only a deleted circle with a wired circle below it moves inputs, so
        // only its references are renumbered and become `[!! input_? !!]`. One
        // below every wired circle (a node's only input, or its last) moves
        // nothing: its references stay as written, and the next edge into that
        // circle is read by them.
        const lastWired = Math.max(
            -1,
            ...survivors.filter((e) => e.target === target.id).map((e) => inputSlotOf(e.targetHandle)),
        );
        const rewritten = removed
            .filter((slot) => slot < lastWired)
            .reduce((text, slot) => renumberInputReferences(text, slot, language), code);
        let inputSlots: unknown[] = target.data?.inputSlots ?? [];
        let sourceSlots: unknown[] = target.data?.sourceSlots ?? [];
        for (const slot of removed) {
            inputSlots = withoutSlot(inputSlots, slot);
            sourceSlots = withoutSlot(sourceSlots, slot);
        }
        const wired = Array.from(new Set(Array.from(handles.values()).map(inputSlotOf).filter((s) => s >= 0)))
            .sort((a, b) => a - b);

        if (moved.size > 0) {
            setEdges((eds) => eds.map((e) => (moved.has(e.id) ? { ...e, targetHandle: moved.get(e.id) } : e)));
        }
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id !== target.id) return node;
                const data = { ...node.data, inputSlots, sourceSlots, input: nodeInputFromSlots(inputSlots, wired) };
                if (rewritten !== code) {
                    data.code = rewritten;
                    data.defaultCode = rewritten;
                }
                return { ...node, data };
            }),
        );
    };

    /** A growing node's circles that *deleted* emptied, taken out of the
     *  values it holds, as another tab's removal leaves them: that tab moved
     *  the edges below and rewrote the code, which the doc brings. */
    const dropCircles = (target: Node, deleted: Edge[]) => {
        const removed = deleted
            .map((e) => inputSlotOf(e.targetHandle))
            .filter((s) => s >= 0)
            .sort((a, b) => b - a);
        if (removed.length === 0) return;
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id !== target.id) return node;
                let inputSlots: unknown[] = node.data?.inputSlots ?? [];
                let sourceSlots: unknown[] = node.data?.sourceSlots ?? [];
                for (const slot of removed) {
                    inputSlots = withoutSlot(inputSlots, slot);
                    sourceSlots = withoutSlot(sourceSlots, slot);
                }
                const wired = wiredInputSlots(
                    reactFlow.getEdges().filter((e) => !deleted.some((d) => d.id === e.id)),
                    node.id,
                );
                return { ...node, data: { ...node.data, inputSlots, sourceSlots, input: nodeInputFromSlots(inputSlots, wired) } };
            }),
        );
    };

    const onEdgesDelete = useCallback(
        (connections: Edge[], options?: { remote?: boolean }) => {
            const deletedIds = new Set(connections.map((c) => c.id));
            const growing = new Map<string, Edge[]>();
            for (const connection of connections) {
                const resetInput = connection.target;
                const targetNode = reactFlow.getNode(connection.target) as Node;
                markNodeStaleRef.current(connection.target);

                // skiping syncronized connections
                if (
                    connection.sourceHandle != "in/out" &&
                    connection.targetHandle != "in/out"
                ) {
                    TrillGenerator.addNewVersionProvenance(
                        reactFlow.getNodes(),
                        reactFlow.getEdges().filter((e: Edge) => e.id !== connection.id),
                        workflowNameRef.current, "", "Connection deleted"
                    );
                }

                // skiping syncronized connections
                if (
                    connection.sourceHandle != "in/out" ||
                    connection.targetHandle != "in/out"
                ) {
                    if (nodeGrowsInputs(targetNode)) {
                        growing.set(connection.target, [...(growing.get(connection.target) ?? []), connection]);
                        continue;
                    }
                    setNodes((nds: any) =>
                        nds.map((node: any) => {
                            if (node.id !== resetInput) return node;
                            const data = { ...node.data, input: "", source: "" };
                            if (nodeHasSeveralPorts(node)) {
                                data.portInputs = withPortValues(node.data.portInputs, [connection.targetHandle ?? "in"], "");
                            }
                            return { ...node, data };
                        })
                    );
                }
            }
            growing.forEach((deleted, targetId) => {
                const target = reactFlow.getNode(targetId);
                if (!target) return;
                if (options?.remote) dropCircles(target, deleted);
                else closeUpCircles(target, deleted, deletedIds);
            });
        },
        [setNodes, setEdges]
    );

    const onNodesDelete = useCallback(
        (changes: NodeChange[]) => {
            setOutputs((opts: any) =>
                opts.filter((opt: any) => {
                    for (const change of changes) {
                        if (change.type === "remove" && 'id' in change) {
                            if (opt.nodeId === change.id) {
                                // node was removed
                                return false;
                            }
                        }
                    }
                    return true;
                })
            );

            for (const change of changes) {
                if (change.type === "remove" && 'id' in change) {
                    const node = reactFlow.getNode(change.id) as Node;
                    if (node) {
                        TrillGenerator.addNewVersionProvenance(
                            reactFlow.getNodes().filter((n: Node) => n.id !== change.id),
                            reactFlow.getEdges(),
                            workflowNameRef.current, "", "Node deleted"
                        );
                    }
                }
            }
        },
        [setOutputs, reactFlow]
    );

    return { applyNodeContent, addNode, propagateDownstreamInputs, applyOutput, onEdgesDelete, onNodesDelete };
}
